import { notifications } from '@mantine/notifications';
import { AssessmentScenarioData } from '../../../../types/assessments';
import { ChatbotOrigin } from '../../../../types/chatbot';
import { ForcePackageData } from '../../../../types/force-packages';
import {
	EffectorData,
	EmitterData,
	PlatformData,
	SensorData,
	SystemData,
} from '../../../../types/systems';
import { validateEffector } from './validators/validateEffector';
import { validateEmitter } from './validators/validateEmitter';
import { validateForcePackage } from './validators/validateForcePackage';
import { validatePlatform } from './validators/validatePlatform';
import { validateScenario } from './validators/validateScenario';
import { validateSensor } from './validators/validateSensor';
import { validateSystem } from './validators/validateSystem';

export type ValidatedJsonType =
	| AssessmentScenarioData
	| SensorData
	| EmitterData
	| EffectorData
	| PlatformData
	| SystemData
	| ForcePackageData;

export type ValidationFailure = {
	key: string;
	reason: string;
	actual: unknown;
};

export type ValidationResult = {
	isValid: boolean;
	failures: ValidationFailure[];
};

export type Rule = {
	reason: string;
	check: () => boolean;
};

export type FieldRules = {
	key: string;
	actual: unknown;
	rules: Rule[];
};

/**
 * Evaluates every rule for every field and collects all failures.
 * Each failure carries the field key, the human-readable reason for the
 * specific sub-condition that failed, and the actual value that was checked.
 */
export function runRules(fields: FieldRules[]): ValidationResult {
	const failures: ValidationFailure[] = [];
	for (const field of fields) {
		for (const rule of field.rules) {
			if (field.key === 'cost') {
				console.log(
					`Validating field: ${field.key}, actual value: ${field.actual}, rule reason: ${rule.reason}`
				);
			}
			if (!rule.check()) {
				failures.push({ key: field.key, reason: rule.reason, actual: field.actual });
			}
		}
	}
	return { isValid: failures.length === 0, failures };
}

// ---------------------------------------------------------------------------
// Reusable rule builders
// ---------------------------------------------------------------------------

export const rules = {
	isString: (v: unknown): Rule => ({
		reason: 'must be a string',
		check: () => typeof v === 'string',
	}),
	isNumber: (v: unknown): Rule => ({
		reason: 'must be a number',
		check: () => typeof v === 'number',
	}),
	isBoolean: (v: unknown): Rule => ({
		reason: 'must be a boolean',
		check: () => typeof v === 'boolean',
	}),
	isArray: (v: unknown): Rule => ({ reason: 'must be an array', check: () => Array.isArray(v) }),
	isObject: (v: unknown): Rule => ({
		reason: 'must be a non-null object',
		check: () => typeof v === 'object' && v !== null && !Array.isArray(v),
	}),
	noSpaces: (v: unknown): Rule => ({
		reason: 'must not contain spaces',
		check: () => typeof v === 'string' && !v.includes(' '),
	}),
	notEmpty: (v: unknown): Rule => ({
		reason: 'must not be empty',
		check: () => Array.isArray(v) && v.length > 0,
	}),
	notBlank: (v: unknown): Rule => ({
		reason: 'must not be blank',
		check: () => typeof v === 'string' && v.trim().length > 0,
	}),
	when: (cond: boolean, rule: Rule): Rule => ({
		reason: rule.reason,
		check: () => !cond || rule.check(),
	}),
	/** Wraps another rule so it passes when the value is `undefined`. */
	optional: (v: unknown, rule: Rule): Rule => ({
		reason: `if defined, ${rule.reason}`,
		check: () => v === undefined || rule.check(),
	}),
	/** Each array item must satisfy `predicate`. Fails (vacuously true) if not an array. */
	everyItem: (v: unknown, reason: string, predicate: (item: unknown) => boolean): Rule => ({
		reason,
		check: () => Array.isArray(v) && v.every(predicate),
	}),
	/** Custom rule for one-off conditions. */
	custom: (reason: string, check: () => boolean): Rule => ({ reason, check }),
};

const validatorsMap: Partial<Record<ChatbotOrigin, (data: ValidatedJsonType) => ValidationResult>> =
	{
		'force-package': validateForcePackage,
		emitter: validateEmitter,
		effector: validateEffector,
		sensor: validateSensor,
		platform: validatePlatform,
		scenario: validateScenario,
		system: validateSystem,
	};

export const validateJson = (json: ValidatedJsonType, origin: ChatbotOrigin): boolean => {
	try {
		const validator = validatorsMap[origin];
		if (!validator) {
			throw new Error(`No validator found for origin: ${origin}`);
		}
		const { isValid, failures } = validator(json);

		if (!isValid) {
			console.error(`Validation failed for origin: ${origin}`);
			console.group('Failed fields:');
			failures.forEach(({ key, reason, actual }) => {
				console.error(`❌ ${key} — ${reason}`, { actual });
			});
			console.groupEnd();

			for (const { key, reason } of failures) {
				notifications.show({
					title: `Invalid field: ${key}`,
					message: `${reason}.`,
					color: 'red',
				});
			}

			return false;
		}

		return isValid;
	} catch (error) {
		return false;
	}
};
